import React, { useRef } from 'react';
import { useLinkBasenameWithRoot } from '@gogame/screens';
import {
  Action,
  Actions,
  CloseIcon,
  RateReviewOutlinedIcon,
  LaunchIcon,
  PostAddIcon,
  HistoryIcon,
  DraggableDialog,
  AIIcon,
  Typography,
} from '@gogame/components';

import {
  useChatContext,
  withChatProvider,
  useFocusInput,
  ExistingChat,
  NewChat,
} from 'components/AIChatbot';
import HistorySidePage from 'scenes/AIChatbot/components/HistorySidePage';
import { aiChatbotLink } from 'scenes/AIChatbot/navigation';

import { useFeedback, useIsUpdating } from 'scenes/AIChatbot/data';
import { useCreateMessage, useLoadMessages } from 'scenes/AIChatbot/data';
import Message from 'scenes/AIChatbot/components/Message';

export const GlobalAIChatDialog = withChatProvider(({ onClose, onExited }) => {
  const { chatId, setChatId, openChildComponent } = useChatContext();
  const ref = useRef(null);
  const chatbotScreenBasename = useLinkBasenameWithRoot(aiChatbotLink);
  const [feedbackChat] = useFeedback();
  const focusInput = useFocusInput();
  const isUpdating = useIsUpdating();
  const handleOpenNewChat = () => {
    setChatId(null);
  };

  const handleClose = () => {
    onClose();
    onExited();
  };

  const handleOpenChatInNewTab = () => {
    const link = aiChatbotLink.stringify(
      {
        id: chatId,
      },
      {
        basename: chatbotScreenBasename,
      }
    );
    window.open(link, '_blank');
  };

  const handleFeedback = async () => {
    await feedbackChat({
      chatId,
    });
  };
  const handleOpenHistory = async () => {
    await openChildComponent(HistorySidePage, {
      currentChatId: chatId,
      onSelectChat: (newChatId) => {
        setChatId(newChatId);
        focusInput();
      },
    });
  };

  const [createMessage, { isLoading }] = useCreateMessage();

  const { isRefetching, data: messages, isFetched } = useLoadMessages(chatId);

  const handleSubmitMessage = async ({ query }) => {
    await createMessage({ query });
  };

  return (
    <DraggableDialog width={460} ref={ref}>
      <DraggableDialog.Toolbar
        icon={<AIIcon sx={{ color: 'text.icon' }} />}
        title={'Chat with AI Agent'}
        primaryActions={
          <Actions>
            <Action
              tooltip={'Open in separate tab'}
              size={'small'}
              icon={<LaunchIcon />}
              onClick={handleOpenChatInNewTab}
            />
            {chatId ? (
              <Action
                tooltip={'New chat'}
                size={'small'}
                icon={<PostAddIcon />}
                onClick={handleOpenNewChat}
              />
            ) : null}
            {chatId ? (
              <Action
                tooltip={'Submit Feedback'}
                size={'small'}
                icon={<RateReviewOutlinedIcon />}
                onClick={handleFeedback}
              />
            ) : null}
            <Action
              tooltip={'History'}
              size={'small'}
              icon={<HistoryIcon />}
              onClick={handleOpenHistory}
            />
            <Action
              tooltip={'Close'}
              size={'small'}
              icon={<CloseIcon size={'small'} />}
              onClick={handleClose}
            />
          </Actions>
        }
      />
      {chatId || isUpdating ? (
        <ExistingChat
          isLoading={isLoading}
          isRefetching={isRefetching}
          isFetched={isFetched}
          messages={messages}
          onSubmitMessage={handleSubmitMessage}
          slots={{
            message: Message,
          }}
        />
      ) : (
        <NewChat isLoading={isLoading} onSubmitMessage={handleSubmitMessage}>
          <Typography variant={'h6'} color={'text.primary'} align={'center'}>
            Hi, welcome to GoGame help desk expert.
          </Typography>
          <Typography variant={'body1'} align={'center'}>
            I'm here to answer any of your GoGame capability questions.
          </Typography>
          <Typography variant={'body1'} align={'center'}>
            What can I assist you with today?
          </Typography>
        </NewChat>
      )}
    </DraggableDialog>
  );
});
